import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { logout } from '../../store/slices/authSlice';
import {
  User,
  LogOut,
  ChevronDown,
  LayoutDashboard,
  Calendar,
  FileText,
  IndianRupee,
  CheckCircle,
  TrendingUp,
  BookOpen,
  Package,
  Bell,
} from 'lucide-react';
import api from '../../services/api/api';
import ThemeToggle from '../theme/ThemeToggle';
import { BREAK_LIMITS } from '../../pages/employee/attendance/utils/formatters';

const CHICAGO_TZ = 'America/Chicago';
const CST_CLOCK_INTERVAL_MS = 15000;
const formatTimerMMSS = (totalSeconds) => {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const mm = Math.floor(safe / 60);
  const ss = safe % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
};

function leaveInboxPath(roleName) {
  if (['HR', 'Admin', 'Super Admin'].includes(roleName)) return '/hr/approvals/leaves';
  if (roleName === 'TL' || roleName === 'Resume Marketing TL') return '/tl/approvals';
  if (roleName === 'Manager') return '/manager/approvals';
  return '/employee/leave';
}

/** e.g. "04/21/2026, 09:45 PM" in America/Chicago (CST/CDT per date). */
function formatChicagoClockLabel(date) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: CHICAGO_TZ,
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  const parts = dtf.formatToParts(date);
  const map = {};
  for (const { type, value } of parts) {
    if (type !== 'literal') map[type] = value;
  }
  const dayPeriod = String(map.dayPeriod || '').toUpperCase();
  return `${map.month}/${map.day}/${map.year}, ${map.hour}:${map.minute} ${dayPeriod}`;
}

/** Wall time in Chicago as `YYYY-MM-DDTHH:mm:ss` for `<time dateTime>`. */
function formatChicagoDateTimeAttr(date) {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: CHICAGO_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
    .format(date)
    .replace(' ', 'T');
}

const Header = () => {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState(null);
  const [sessionResolved, setSessionResolved] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  const [cstClock, setCstClock] = useState(() => ({
    label: formatChicagoClockLabel(new Date()),
    dateTime: formatChicagoDateTimeAttr(new Date()),
  }));
  const [endingBreak, setEndingBreak] = useState(false);
  const [myCompanyOpen, setMyCompanyOpen] = useState(false);
  const myCompanyRef = useRef(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifItems, setNotifItems] = useState([]);
  const [notifUnread, setNotifUnread] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);
  const notifRef = useRef(null);
  const companyName =
    user?.company?.name ||
    user?.companyName ||
    user?.organizationName ||
    'My Company';
  const canShowMyCompanyMenu = ['Employee', 'TL', 'HR'].includes(user?.role?.name);
  const myCompanyMenuItems = [
    { label: 'Attendance', path: '/employee/attendance', icon: Calendar },
    { label: 'Profile', path: '/employee/profile', icon: User },
    { label: 'Leave', path: '/employee/leave', icon: FileText },
    { label: 'Payslip', path: '/employee/payroll', icon: IndianRupee },
    { label: 'Tasks', path: '/employee/tasks', icon: CheckCircle },
    { label: 'Performance', path: '/employee/performance', icon: TrendingUp },
    { label: 'Learning', path: '/employee/learning', icon: BookOpen },
    { label: 'Assets', path: '/employee/assets', icon: Package },
  ];

  const refreshSession = async () => {
    const cacheKey = `header_session_v1_${user?._id || user?.employee?._id || 'me'}`;
    try {
      const response = await api.get('/attendance/current-session');
      const latest = response?.data?.data || null;
      setSession(latest);
      sessionStorage.setItem(cacheKey, JSON.stringify(latest));
    } catch {
      setSession(null);
      sessionStorage.removeItem(cacheKey);
    } finally {
      setSessionResolved(true);
    }
  };

  useEffect(() => {
    const cacheKey = `header_session_v1_${user?._id || user?.employee?._id || 'me'}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        setSession(JSON.parse(cached));
      } catch {
        /* ignore invalid cache */
      }
    }
    refreshSession();
    const poll = setInterval(refreshSession, 60000);
    const onSessionUpdate = () => {
      refreshSession();
    };
    window.addEventListener('attendance-session-updated', onSessionUpdate);
    return () => {
      clearInterval(poll);
      window.removeEventListener('attendance-session-updated', onSessionUpdate);
    };
  }, [user?._id, user?.employee?._id]);

  useEffect(() => {
    const timer = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const tick = () => {
      const d = new Date();
      setCstClock({ label: formatChicagoClockLabel(d), dateTime: formatChicagoDateTimeAttr(d) });
    };
    tick();
    const id = setInterval(tick, CST_CLOCK_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const fetchNotifUnread = async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      setNotifUnread(res?.data?.data?.count ?? 0);
    } catch {
      setNotifUnread(0);
    }
  };

  useEffect(() => {
    fetchNotifUnread();
    const poll = setInterval(fetchNotifUnread, 120000);
    return () => clearInterval(poll);
  }, []);

  const openNotifications = async () => {
    const next = !notifOpen;
    setNotifOpen(next);
    if (!next) return;
    setNotifLoading(true);
    try {
      const res = await api.get('/notifications?limit=40');
      setNotifItems(res?.data?.data || []);
      await fetchNotifUnread();
    } catch {
      setNotifItems([]);
    } finally {
      setNotifLoading(false);
    }
  };

  const handleNotifItemClick = async (n) => {
    try {
      if (!n.read) {
        await api.patch(`/notifications/${n._id}/read`);
      }
    } catch {
      /* ignore */
    }
    setNotifOpen(false);
    if (n.leave) {
      navigate(leaveInboxPath(user?.role?.name));
    } else if (n.type === 'attendance_checkout_reminder') {
      navigate('/employee/attendance');
    } else if (n.type === 'policy_added') {
      navigate('/employee/profile');
    }
    fetchNotifUnread();
  };

  const handleMarkAllNotifRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifItems((prev) => prev.map((x) => ({ ...x, read: true })));
      setNotifUnread(0);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (myCompanyRef.current && !myCompanyRef.current.contains(event.target)) {
        setMyCompanyOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setNotifOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const breakStartTs = session?.activeBreak?.startedAt
    ? new Date(session.activeBreak.startedAt).getTime()
    : null;
  const breakInProgress = Boolean(
    sessionResolved && session?.checkIn && !session?.checkOut && breakStartTs
  );

  const breakTimer = useMemo(() => {
    if (!breakInProgress || !breakStartTs) {
      return { label: '00:00', isOvertime: false };
    }
    const elapsedSec = Math.max(0, Math.floor((nowTs - breakStartTs) / 1000));
    const breakNumber = Number(session?.activeBreak?.number) || 0;
    const limitMin = BREAK_LIMITS[breakNumber] || 0;
    const remainingSec = limitMin * 60 - elapsedSec;
    if (remainingSec >= 0) {
      return { label: formatTimerMMSS(remainingSec), isOvertime: false };
    }
    return { label: formatTimerMMSS(Math.abs(remainingSec)), isOvertime: true };
  }, [breakInProgress, breakStartTs, nowTs, session?.activeBreak?.number]);

  const userDisplayName = useMemo(() => {
    const emp = user?.employee;
    if (emp && (emp.firstName || emp.lastName)) {
      return `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
    }
    return user?.email || 'User';
  }, [user?.employee, user?.email]);

  const pageTitle = useMemo(() => {
    const pathname = String(location.pathname || '').trim();
    if (!pathname || pathname === '/') return 'Dashboard';
    const parts = pathname.split('/').filter(Boolean);
    if (!parts.length) return 'Dashboard';
    const labels = parts
      .slice(0, 3)
      .map((part) =>
        part
          .split('-')
          .map((w) => (w ? `${w.charAt(0).toUpperCase()}${w.slice(1)}` : w))
          .join(' ')
      );
    return labels.join(' > ');
  }, [location.pathname]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleEndBreak = async () => {
    try {
      setEndingBreak(true);
      const { data } = await api.post('/attendance/break-end');
      if (data?.warning) {
        alert(`${data?.message || 'Break ended.'}\n\nWarning: ${data.warning}`);
      } else {
        alert(data?.message || 'Break ended.');
      }
      await refreshSession();
      window.dispatchEvent(new Event('attendance-session-updated'));
    } catch (error) {
      alert(error.response?.data?.message || 'Could not end break');
    } finally {
      setEndingBreak(false);
    }
  };

  const handleMyCompanyNavigate = (path) => {
    navigate(path);
    setMyCompanyOpen(false);
  };

  return (
    <header className="sticky top-0 z-10 h-16 hrms-header border-b border-[var(--border-color)] px-4 sm:px-6 flex justify-between items-center gap-3 transition-colors duration-300">
      <div className="flex items-center gap-3">
        <div className="inline-flex items-center gap-2 min-w-0">
          <LayoutDashboard className="w-5 h-5 sm:w-6 sm:h-6 text-[var(--icon-primary)] shrink-0" aria-hidden />
          <h1 className="text-base sm:text-xl font-semibold text-content-primary truncate">{companyName}</h1>
        </div>
        <span className="text-content-muted hidden sm:inline">/</span>
        <div className="text-sm font-medium text-content-secondary truncate max-w-[8rem] sm:max-w-[24rem] hidden md:block" title={pageTitle}>
          {pageTitle}
        </div>
        {canShowMyCompanyMenu && sessionResolved && (
          <div className="relative" ref={myCompanyRef}>
            <button
              type="button"
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md border text-sm font-medium ${
                breakInProgress
                  ? 'btn-disabled'
                  : 'text-content-secondary border-[var(--border-color)] hover:bg-[var(--bg-hover)]'
              }`}
              onClick={() => setMyCompanyOpen((prev) => !prev)}
              disabled={breakInProgress}
              aria-label="Open company menu"
              title={breakInProgress ? 'Navigation is disabled while break is active' : 'Open My Company menu'}
            >
              <ChevronDown className={`w-4 h-4 transition-transform ${myCompanyOpen ? 'rotate-180' : ''}`} />
            </button>
            {myCompanyOpen && (
              <div className="absolute left-0 top-full mt-2 w-52 dropdown-panel z-40 py-1">
                {myCompanyMenuItems.map((item) => (
                  <button
                    key={item.path}
                    type="button"
                    className="dropdown-item flex items-center gap-2"
                    onClick={() => handleMyCompanyNavigate(item.path)}
                  >
                    <item.icon className="w-4 h-4 icon-theme shrink-0" />
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {canShowMyCompanyMenu && !sessionResolved && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm text-content-muted border-[var(--border-color)] bg-[var(--bg-muted)]">
            Loading menu...
          </div>
        )}
        {breakInProgress && (
          <div className="flex items-center gap-2">
            <span
              className={`px-3 py-1 rounded-full text-sm border ${
                breakTimer.isOvertime
                  ? 'bg-red-100 text-red-900 border-red-300'
                  : 'bg-green-100 text-green-900 border-green-300'
              }`}
            >
              {breakTimer.isOvertime ? `Break overtime: ${breakTimer.label}` : `Break left: ${breakTimer.label}`}
            </span>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleEndBreak}
              disabled={endingBreak}
            >
              {endingBreak ? 'Ending...' : 'End Break'}
            </button>
          </div>
        )}
      </div>
      <div className="flex items-center space-x-4">
        <ThemeToggle />
        <div className="relative" ref={notifRef}>
          <button
            type="button"
            onClick={openNotifications}
            className="relative p-2 rounded-md border border-[var(--border-color)] text-content-secondary hover:bg-[var(--bg-hover)] transition-colors duration-300"
            aria-label="Notifications"
            title="Notifications"
          >
            <Bell className="w-5 h-5 text-[var(--icon-primary)]" />
            {notifUnread > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[1.125rem] h-[1.125rem] px-1 flex items-center justify-center rounded-full bg-red-600 text-content-onaccent text-[10px] font-semibold">
                {notifUnread > 99 ? '99+' : notifUnread}
              </span>
            )}
          </button>
          {notifOpen && (
            <div className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] dropdown-panel z-50 flex flex-col max-h-[min(24rem,70vh)]">
              <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border-color)]">
                <span className="text-sm font-semibold text-content-primary">Notifications</span>
                {notifUnread > 0 && (
                  <button
                    type="button"
                    className="text-xs text-indigo-600 hover:text-indigo-800"
                    onClick={handleMarkAllNotifRead}
                  >
                    Mark all read
                  </button>
                )}
              </div>
              <div className="overflow-y-auto flex-1">
                {notifLoading && (
                  <div className="px-3 py-6 text-center text-sm text-content-muted">Loading…</div>
                )}
                {!notifLoading && notifItems.length === 0 && (
                  <div className="px-3 py-6 text-center text-sm text-content-muted">No notifications</div>
                )}
                {!notifLoading &&
                  notifItems.map((n) => (
                    <button
                      key={n._id}
                      type="button"
                      onClick={() => handleNotifItemClick(n)}
                      className={`w-full text-left px-3 py-2.5 border-b border-[var(--border-color)] hover:bg-[var(--bg-hover)] ${
                        n.read ? '' : 'bg-[var(--bg-active)]'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm font-medium text-content-primary">{n.title}</span>
                        {!n.read && <span className="shrink-0 w-2 h-2 rounded-full bg-indigo-500 mt-1.5" aria-hidden />}
                      </div>
                      <p className="text-xs text-content-secondary mt-0.5 line-clamp-2">{n.body}</p>
                      <p className="text-[10px] text-content-muted mt-1">
                        {new Date(n.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST
                      </p>
                    </button>
                  ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-start gap-2">
          <User className="w-5 h-5 text-[var(--icon-primary)] shrink-0 mt-0.5" aria-hidden />
          <div className="flex flex-col items-start min-w-0">
            <span className="text-content-secondary font-medium truncate max-w-[14rem] sm:max-w-xs" title={userDisplayName}>
              {userDisplayName}
            </span>
            <time
              className="text-xs text-content-muted tabular-nums mt-0.5"
              dateTime={cstClock.dateTime}
              title="America/Chicago (Central Time)"
            >
              {cstClock.label}
            </time>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className={`flex items-center space-x-2 ${
            breakInProgress
              ? 'text-content-muted cursor-not-allowed'
              : 'text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300'
          }`}
          disabled={breakInProgress}
          title={breakInProgress ? 'Logout disabled while break is active' : 'Logout'}
        >
          <LogOut className="w-5 h-5" />
          <span>Logout</span>
        </button>
      </div>
    </header>
  );
};

export default Header;
